import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Textarea } from '@rudi/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader } from '../components/page';
import { usePracticeAgent, usePrescreeningQuery } from '../api/hooks';
import { LiveDeviceError, LiveViewError, type LiveSession } from '../live-view/session';
import { useLiveSession } from '../live-view/useLiveSession';
import type {
  LiveCriterionResult,
  LiveEvidence,
  LiveFunnelStage,
  LiveRow,
  LiveRun,
  LiveSqlResult,
  LiveSqlValue,
} from '../live-view/types';
import { Section, agoText, dateText } from './practice-agents';
import { CERTAINTY_TEXT, SEX_TEXT, stageText } from './criteria-query';
import { DraftBadge, MarkAsWrongLink, shareText } from './prescreening';

// The live view: a run's pseudonymized, ranked rows and read-only SQL on the
// practice computer's coded.db, end-to-end encrypted between this page and
// the practice computer. Everything it receives lives in component state and
// goes with the session; nothing reaches React Query, localStorage or
// sessionStorage.

export interface LiveViewSearch {
  /** Rudi's ID of the run the view opened from. */
  run?: string;
  /** The practice computer's ID of that run. */
  deviceRun?: string;
}

export function validateLiveViewSearch(search: Record<string, unknown>): LiveViewSearch {
  const text = (value: unknown) =>
    typeof value === 'string' && value
      ? value
      : typeof value === 'number'
        ? String(value)
        : undefined;
  return { run: text(search.run), deviceRun: text(search.deviceRun) };
}

export function LiveViewRoutePage() {
  const { deviceId } = useParams({ from: '/practice-agents/$deviceId/live' });
  const search = useSearch({ from: '/practice-agents/$deviceId/live' });
  return <LiveViewPage deviceId={deviceId} runId={search.run} deviceRunId={search.deviceRun} />;
}

export const LEGAL_GATE =
  'Use with real patient data only after the processing agreement covers the live view and the § 393 SGB V answer is in.';

export function LiveViewPage({
  deviceId,
  runId,
  deviceRunId,
}: {
  deviceId: string;
  runId?: string;
  deviceRunId?: string;
}) {
  const device = usePracticeAgent(deviceId);
  const [attempt, setAttempt] = useState(0);
  const live = useLiveSession({ deviceId, runId }, attempt);
  const name = device.data?.practiceName ?? 'Practice computer';

  return (
    <div>
      {runId ? (
        <Link
          to="/prescreening/runs/$runId"
          params={{ runId }}
          className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Pre-screening run
        </Link>
      ) : (
        <Link
          to="/practice-agents/$deviceId"
          params={{ deviceId }}
          className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Practice computer
        </Link>
      )}
      <PageHeader
        title="Live view"
        description={`${name}. Pseudonymized rows and read-only SQL, live from the practice computer.`}
        actions={
          live.phase === 'ready' && (
            <Button variant="outline" onClick={() => void live.session.close()}>
              Close live view
            </Button>
          )
        }
      />
      <LiveViewNotice />

      {live.phase === 'opening' && (
        <p role="status" className="py-8 text-center text-sm text-muted-foreground">
          {live.waiting ? 'Waiting for the practice computer to answer…' : 'Opening the live view…'}
        </p>
      )}
      {live.phase === 'ended' && (
        <Ended
          error={live.error}
          lastSeenAt={device.data?.lastSeenAt}
          onOpenAgain={() => setAttempt(value => value + 1)}
        />
      )}
      {live.phase === 'ready' && (
        <LiveData key={live.session.sessionId} session={live.session} initialRun={deviceRunId} />
      )}
    </div>
  );
}

function LiveViewNotice() {
  return (
    <aside
      aria-label="About the live view"
      className="mb-6 space-y-2 rounded-3xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm"
    >
      <p>
        The rows are pseudonymized, not anonymous. The practice can link each pseudonym back to its
        patient, so everything here is personal data.
      </p>
      <p>
        The practice computer encrypts every answer for this page alone. Rudi relays only ciphertext
        and stores none of it, and this page keeps nothing once it closes. Cortex logs each page
        view and SQL statement, and so does the practice computer.
      </p>
      <p className="font-medium">{LEGAL_GATE}</p>
    </aside>
  );
}

function Ended({
  error,
  lastSeenAt,
  onOpenAgain,
}: {
  error: LiveViewError | null;
  lastSeenAt?: string | null;
  onOpenAgain: () => void;
}) {
  if (error?.code === 'unavailable')
    return (
      <p role="alert" className="py-8 text-center text-sm">
        {error.message}
      </p>
    );
  return (
    <div className="space-y-3 py-8 text-center text-sm">
      {error ? (
        <p role="alert" className="text-destructive">
          {error.message}
          {error.code === 'device_offline' &&
            lastSeenAt &&
            ` It was last seen ${agoText(lastSeenAt)}. The run's counts stay on the run page.`}
        </p>
      ) : (
        <p role="status">The live view is closed. Nothing from it stays in this browser.</p>
      )}
      <Button variant="outline" onClick={onOpenAgain}>
        Open again
      </Button>
    </div>
  );
}

const SQL_ERROR_TEXT: Record<string, string> = {
  not_read_only: 'The statement could change data. Only read-only statements run.',
  not_select: 'The statement must start with SELECT, WITH or VALUES.',
  multiple_statements: 'Send one statement at a time.',
  sql_error: 'SQLite could not run the statement. Check the table and column names.',
  timeout: 'The statement ran for 5 minutes, so the practice computer stopped it.',
  output_filter: "The practice computer's output filter held the result back.",
  unknown_run: 'The practice computer does not have this run any more.',
};

interface RequestFailure {
  text: string;
  errorClass?: string;
}

function requestFailure(error: unknown): RequestFailure {
  if (error instanceof LiveDeviceError)
    return {
      text: SQL_ERROR_TEXT[error.errorClass] ?? 'The practice computer refused the request.',
      errorClass: error.errorClass,
    };
  if (error instanceof LiveViewError) return { text: error.message };
  return { text: 'The request failed.' };
}

function FailureText({ failure }: { failure: RequestFailure }) {
  return (
    <p role="alert" className="text-sm text-destructive">
      {failure.text}
      {failure.errorClass && (
        <span className="ml-2 text-xs text-muted-foreground">
          Error class <code>{failure.errorClass}</code>
        </span>
      )}
    </p>
  );
}

function numberText(value: number | null | undefined): string {
  return typeof value === 'number' ? value.toLocaleString() : 'n/a';
}

/** Ignores answers that arrive after the component holding them went away. */
function useAlive() {
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  return alive;
}

function LiveData({ session, initialRun }: { session: LiveSession; initialRun?: string }) {
  const alive = useAlive();
  const [runs, setRuns] = useState<LiveRun[] | null>(null);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [selected, setSelected] = useState<string | null>(initialRun ?? null);

  useEffect(() => {
    session.runs().then(
      list => alive.current && setRuns(list),
      (error: unknown) => alive.current && setFailure(requestFailure(error))
    );
  }, [session, alive]);

  const run = runs?.find(item => item.runId === selected) ?? null;
  const closes = new Date(session.closesAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      <p role="status" className="mb-6 text-xs text-muted-foreground">
        Connected. The practice computer signed the session key with its device key{' '}
        <code>{session.deviceKeyId}</code>. The view closes by {closes}.
      </p>

      <Section
        title="Runs on the practice computer"
        description="Exact numbers, straight from the practice computer."
      >
        {failure ? (
          <FailureText failure={failure} />
        ) : runs === null ? (
          <p className="text-sm text-muted-foreground">Asking for the runs…</p>
        ) : (
          <LiveRunsTable runs={runs} selected={selected} onSelect={setSelected} />
        )}
        {runs && selected && !run && (
          <p className="text-sm text-muted-foreground">
            The practice computer no longer has the run this view opened from. Pick another.
          </p>
        )}
      </Section>

      {run && <RunRows key={run.runId} session={session} run={run} />}

      <SqlConsole session={session} />
    </>
  );
}

function LiveRunsTable({
  runs,
  selected,
  onSelect,
}: {
  runs: LiveRun[];
  selected: string | null;
  onSelect: (runId: string) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-3xl border">
      <Table aria-label="Runs on the practice computer">
        <TableHeader>
          <TableRow>
            <TableHead>Finished</TableHead>
            <TableHead>Trial</TableHead>
            <TableHead>Query version</TableHead>
            <TableHead className="text-right">Screened</TableHead>
            <TableHead className="text-right">Candidates</TableHead>
            <TableHead className="text-right">Needs review</TableHead>
            <TableHead className="text-right">Excluded</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map(run => {
            const current = run.runId === selected;
            return (
              <TableRow key={run.runId} className={current ? 'bg-accent/50' : undefined}>
                <TableCell className="min-w-[10rem]">{dateText(run.finishedAt)}</TableCell>
                <TableCell className="min-w-[12rem]">{run.trialTitle || 'Unknown trial'}</TableCell>
                <TableCell className="min-w-[8rem]">
                  Version {run.version}
                  {run.draft && (
                    <span className="ml-2">
                      <DraftBadge />
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">{numberText(run.totals?.screened)}</TableCell>
                <TableCell className="text-right">{numberText(run.totals?.candidates)}</TableCell>
                <TableCell className="text-right">{numberText(run.totals?.needsReview)}</TableCell>
                <TableCell className="text-right">{numberText(run.totals?.excluded)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant={current ? 'default' : 'outline'}
                    aria-pressed={current}
                    aria-label={`Show rows of ${run.trialTitle || 'this run'}, version ${run.version}`}
                    onClick={() => onSelect(run.runId)}
                  >
                    Show rows
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {runs.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="h-20 text-center text-muted-foreground">
                The practice computer has no runs yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface RowPages {
  rows: LiveRow[];
  funnel: LiveFunnelStage[];
  next: string | null;
  loading: boolean;
  started: boolean;
  failure: RequestFailure | null;
}

const NO_PAGES: RowPages = {
  rows: [],
  funnel: [],
  next: null,
  loading: false,
  started: false,
  failure: null,
};

/** Pages through a run's rows, or through its excluded sample. */
function useRowPages(session: LiveSession, runId: string, excludedSample: boolean) {
  const alive = useAlive();
  const [pages, setPages] = useState<RowPages>(NO_PAGES);
  const load = useCallback(
    async (cursor?: string) => {
      setPages(current => ({ ...current, loading: true, started: true, failure: null }));
      try {
        const page = await session.rows({ runId, cursor, excludedSample });
        if (!alive.current) return;
        setPages(current => ({
          rows: cursor ? [...current.rows, ...page.rows] : page.rows,
          funnel: page.funnel?.length ? page.funnel : current.funnel,
          next: page.nextCursor ?? null,
          loading: false,
          started: true,
          failure: null,
        }));
      } catch (error) {
        if (!alive.current) return;
        setPages(current => ({ ...current, loading: false, failure: requestFailure(error) }));
      }
    },
    [session, runId, excludedSample, alive]
  );
  return { ...pages, load };
}

function RunRows({ session, run }: { session: LiveSession; run: LiveRun }) {
  const ranked = useRowPages(session, run.runId, false);
  const excluded = useRowPages(session, run.runId, true);
  const query = usePrescreeningQuery(run.queryId);
  const { load: loadRanked } = ranked;

  useEffect(() => {
    void loadRanked();
  }, [loadRanked]);

  const criterionText = useMemo(() => {
    const version = query.data?.versions?.find(item => item.version === run.version);
    return new Map((version?.criteria ?? []).map(criterion => [criterion.id, criterion.text]));
  }, [query.data, run.version]);

  const title = `${run.trialTitle || 'Unknown trial'}, version ${run.version}`;
  return (
    <>
      <Section
        title="Pseudonymized rows"
        description={`${title}. All candidates and patients who need review, best rank first. Stage 3 criteria show their outcome and confidence only; the quote stays at the practice.`}
        actions={
          <Link
            to="/prescreening/queries/$queryId"
            params={{ queryId: run.queryId }}
            className="text-xs underline-offset-4 hover:underline"
          >
            Criteria query
          </Link>
        }
      >
        {ranked.funnel.length > 0 && <FunnelTable funnel={ranked.funnel} />}
        {ranked.failure && <FailureText failure={ranked.failure} />}
        {ranked.loading && ranked.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Asking for the rows…</p>
        ) : (
          ranked.started &&
          !ranked.failure && (
            <RowsTable label="Ranked rows" rows={ranked.rows} run={run} texts={criterionText} />
          )
        )}
        {ranked.next && (
          <Button
            variant="outline"
            size="sm"
            disabled={ranked.loading}
            onClick={() => void ranked.load(ranked.next ?? undefined)}
          >
            {ranked.loading ? 'Loading…' : 'Load more'}
          </Button>
        )}
      </Section>

      <Section
        title="Excluded sample"
        description="A sample of excluded patients for each criterion, to check that the exclusions are right."
        actions={
          !excluded.started && (
            <Button size="sm" variant="outline" onClick={() => void excluded.load()}>
              Load excluded sample
            </Button>
          )
        }
      >
        {excluded.failure && <FailureText failure={excluded.failure} />}
        {excluded.loading && excluded.rows.length === 0 && (
          <p className="text-sm text-muted-foreground">Asking for the excluded sample…</p>
        )}
        {excluded.started && excluded.rows.length > 0 && (
          <RowsTable label="Excluded sample" rows={excluded.rows} run={run} texts={criterionText} />
        )}
        {excluded.started && !excluded.loading && !excluded.failure && !excluded.rows.length && (
          <p className="text-sm text-muted-foreground">This run excluded nobody.</p>
        )}
        {excluded.next && (
          <Button
            variant="outline"
            size="sm"
            disabled={excluded.loading}
            onClick={() => void excluded.load(excluded.next ?? undefined)}
          >
            {excluded.loading ? 'Loading…' : 'Load more excluded'}
          </Button>
        )}
      </Section>
    </>
  );
}

function FunnelTable({ funnel }: { funnel: LiveFunnelStage[] }) {
  return (
    <div className="overflow-x-auto rounded-3xl border">
      <Table aria-label="Funnel">
        <TableHeader>
          <TableRow>
            <TableHead>Stage</TableHead>
            <TableHead className="text-right">Entered</TableHead>
            <TableHead className="text-right">Removed</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {funnel.map(stage => (
            <TableRow key={stage.stage}>
              <TableCell>{stageText(stage.stage)}</TableCell>
              <TableCell className="text-right">{numberText(stage.entered)}</TableCell>
              <TableCell className="text-right">{numberText(stage.removed)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const OUTCOME_TEXT: Record<string, string> = {
  candidate: 'Candidate',
  needs_review: 'Needs review',
  excluded: 'Excluded',
};

const CRITERION_OUTCOME_TEXT: Record<string, string> = {
  met: 'met',
  not_met: 'not met',
  unknown: 'unknown',
};

function criterionOutcomeText(outcome: string): string {
  return CRITERION_OUTCOME_TEXT[outcome] ?? outcome;
}

/** "3 met, 1 unknown" over a row's criteria. */
function criteriaSummary(criteria: LiveCriterionResult[]): string {
  const counts = new Map<string, number>();
  for (const criterion of criteria)
    counts.set(criterion.outcome, (counts.get(criterion.outcome) ?? 0) + 1);
  return (
    [...counts].map(([outcome, count]) => `${count} ${criterionOutcomeText(outcome)}`).join(', ') ||
    'None'
  );
}

function sexText(sex: string | null | undefined): string {
  return sex ? (SEX_TEXT[sex] ?? sex) : 'n/a';
}

function RowsTable({
  label,
  rows,
  run,
  texts,
}: {
  label: string;
  rows: LiveRow[];
  run: LiveRun;
  texts: Map<string, string>;
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (pseudonym: string) =>
    setOpen(current => {
      const next = new Set(current);
      if (next.has(pseudonym)) next.delete(pseudonym);
      else next.add(pseudonym);
      return next;
    });

  return (
    <div className="overflow-x-auto rounded-3xl border">
      <Table aria-label={label}>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">Rank</TableHead>
            <TableHead>Pseudonym</TableHead>
            <TableHead>Outcome</TableHead>
            <TableHead className="text-right">Confidence</TableHead>
            <TableHead className="text-right">Age</TableHead>
            <TableHead>Sex</TableHead>
            <TableHead>Criteria</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => {
            const criteria = row.criteria ?? [];
            const expanded = open.has(row.pseudonym);
            return (
              <Fragment key={row.pseudonym}>
                <TableRow>
                  <TableCell className="text-right tabular-nums">{numberText(row.rank)}</TableCell>
                  <TableCell>
                    <code className="text-xs">{row.pseudonym}</code>
                  </TableCell>
                  <TableCell>{OUTCOME_TEXT[row.outcome] ?? row.outcome}</TableCell>
                  <TableCell className="text-right">{shareText(row.confidence)}</TableCell>
                  <TableCell className="text-right">{numberText(row.ageYears)}</TableCell>
                  <TableCell>{sexText(row.sex)}</TableCell>
                  <TableCell className="min-w-[14rem]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground">
                        {criteriaSummary(criteria)}
                      </span>
                      {criteria.length > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-expanded={expanded}
                          aria-label={`Criteria of ${row.pseudonym}`}
                          onClick={() => toggle(row.pseudonym)}
                        >
                          {expanded ? 'Hide' : 'Show'}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {expanded && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/30">
                      <CriteriaTable
                        pseudonym={row.pseudonym}
                        criteria={criteria}
                        run={run}
                        texts={texts}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="h-20 text-center text-muted-foreground">
                No candidates and nobody to review in this run.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CriteriaTable({
  pseudonym,
  criteria,
  run,
  texts,
}: {
  pseudonym: string;
  criteria: LiveCriterionResult[];
  run: LiveRun;
  texts: Map<string, string>;
}) {
  return (
    <Table aria-label={`Criteria of ${pseudonym}`}>
      <TableHeader>
        <TableRow>
          <TableHead>Criterion</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead>Stage</TableHead>
          <TableHead className="text-right">Confidence</TableHead>
          <TableHead>Coded evidence</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {criteria.map(criterion => (
          <TableRow key={criterion.id}>
            <TableCell className="min-w-[12rem]">
              <div className="font-medium">{criterion.id}</div>
              {texts.get(criterion.id) && (
                <div className="text-xs text-muted-foreground">{texts.get(criterion.id)}</div>
              )}
            </TableCell>
            <TableCell>{criterionOutcomeText(criterion.outcome)}</TableCell>
            <TableCell className="text-xs">{stageText(criterion.stage)}</TableCell>
            <TableCell className="text-right">{shareText(criterion.confidence)}</TableCell>
            <TableCell className="min-w-[14rem] text-xs">
              <Evidence criterion={criterion} />
            </TableCell>
            <TableCell className="text-right">
              <MarkAsWrongLink
                queryId={run.queryId}
                version={run.version}
                criterionId={criterion.id}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function evidenceText(item: LiveEvidence): string {
  const certainty = item.certainty ? (CERTAINTY_TEXT[item.certainty] ?? item.certainty) : null;
  const value =
    item.value !== null && item.value !== undefined
      ? `${item.value}${item.unit ? ` ${item.unit}` : ''}`
      : null;
  return [
    [item.code, item.system && `(${item.system})`].filter(Boolean).join(' '),
    certainty,
    value,
    item.month,
  ]
    .filter(Boolean)
    .join(', ');
}

function Evidence({ criterion }: { criterion: LiveCriterionResult }) {
  if (criterion.stage === 3)
    return <span className="text-muted-foreground">The quote stays at the practice.</span>;
  const items = criterion.evidence ?? [];
  if (!items.length) return <span className="text-muted-foreground">None</span>;
  return (
    <details>
      <summary className="cursor-pointer">
        {items.length === 1 ? '1 coded fact' : `${items.length} coded facts`}
      </summary>
      <ul className="mt-1 space-y-0.5">
        {items.map((item, index) => (
          <li key={index}>{evidenceText(item)}</li>
        ))}
      </ul>
    </details>
  );
}

const MAX_STATEMENT_LENGTH = 20_000;

function SqlConsole({ session }: { session: LiveSession }) {
  const alive = useAlive();
  const [statement, setStatement] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<LiveSqlResult | null>(null);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const tooLong = statement.length > MAX_STATEMENT_LENGTH;

  const run = async (event?: Pick<FormEvent, 'preventDefault'>) => {
    event?.preventDefault();
    const text = statement.trim();
    if (!text || running || tooLong) return;
    setRunning(true);
    setFailure(null);
    try {
      const answer = await session.sql(text);
      if (!alive.current) return;
      setResult(answer);
    } catch (error) {
      if (!alive.current) return;
      setResult(null);
      setFailure(requestFailure(error));
    } finally {
      if (alive.current) setRunning(false);
    }
  };

  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void run(event);
  };

  return (
    <Section
      title="SQL console"
      description="Read-only SQL on the practice computer's pseudonymized database, coded.db."
    >
      <form className="space-y-3 rounded-3xl border bg-card p-4" onSubmit={run}>
        <Textarea
          aria-label="SQL statement"
          className="min-h-28 font-mono text-xs"
          placeholder="SELECT sex, count(*) FROM patients GROUP BY sex"
          value={statement}
          onChange={event => setStatement(event.target.value)}
          onKeyDown={keyDown}
        />
        <p className="text-xs text-muted-foreground">
          One statement that starts with SELECT, WITH or VALUES. The practice computer writes every
          statement to its audit log, and Cortex logs it too. Local IDs come back as pseudonyms,
          dates as months and birth dates as ages, at most 500 rows.
        </p>
        {tooLong && (
          <p className="text-xs text-destructive">
            Keep the statement under {MAX_STATEMENT_LENGTH.toLocaleString()} characters.
          </p>
        )}
        <Button type="submit" disabled={running || !statement.trim() || tooLong}>
          {running ? 'Running…' : 'Run'}
        </Button>
      </form>
      {failure && <FailureText failure={failure} />}
      {result && <SqlResultTable result={result} />}
    </Section>
  );
}

function cellText(value: LiveSqlValue) {
  if (value === null) return <span className="text-muted-foreground">NULL</span>;
  return String(value);
}

function SqlResultTable({ result }: { result: LiveSqlResult }) {
  const count = result.rows.length;
  return (
    <div className="space-y-2">
      <p role="status" className="text-xs text-muted-foreground">
        {count === 1 ? '1 row' : `${count.toLocaleString()} rows`}
        {result.truncated &&
          '. The practice computer stopped at 500 rows; narrow the statement to see the rest.'}
      </p>
      <div className="max-h-[32rem] overflow-auto rounded-3xl border">
        <Table aria-label="SQL result">
          <TableHeader>
            <TableRow>
              {result.columns.map((column, index) => (
                <TableHead key={`${index}-${column}`}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((value, index) => (
                  <TableCell key={index} className="font-mono text-xs">
                    {cellText(value)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {count === 0 && (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, result.columns.length)}
                  className="h-16 text-center text-muted-foreground"
                >
                  No rows.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
